import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import dynamic from 'next/dynamic';
import BeforeAfterSlider from '@components/BeforeAfterSlider';
import KrpanoViewer from '@components/Krpano/KrpanoViewer';
import { ID_KRPANO_CONTAINER } from '../../constants';
import { useSelector } from 'react-redux';
import {
  selectConfigNadir,
  selectConfigFollowModeProject,
  selectSceneSelected,
  selectListScene,
  selectConfigSoundProject,
  selectPrevSceneSelected,
} from '@containers/Home/selectors';
import { I_Scene } from '@containers/Home/types/I_Scene';
import { Type_RefFuncKrpano } from '@components/Krpano/types';
import {
  useCollaborationMode,
  useTourEventState,
} from '@containers/Home/global/TourStats';
import { useKrpano } from '@containers/Home/hook/useKrpano';

const MultipleStagingThumb = dynamic(
  () => import('@containers/Home/components/MultipleStagingThumb'),
);

interface Props {
  onChangeScene: (scene: I_Scene) => void;
  krpanoRefState: Type_RefFuncKrpano | null;
  setIsTourRendered: (isLoaded: boolean) => void;
  setKrpanoRefState: (krpanoRef: Type_RefFuncKrpano | null) => void;
}

export const TourRenderSceneKrpano = memo(
  ({
    onChangeScene,
    krpanoRefState,
    setKrpanoRefState,
    setIsTourRendered,
  }: Props) => {
    const krpanoRef = useRef<Type_RefFuncKrpano | null>(null!);

    const listScene = useSelector(selectListScene);
    const nadirConfig = useSelector(selectConfigNadir);
    const sceneSelected = useSelector(selectSceneSelected);
    const configSound = useSelector(selectConfigSoundProject);
    const prevSceneSelected = useSelector(selectPrevSceneSelected);
    const configFollowMode = useSelector(selectConfigFollowModeProject);

    const [, setKrpanoInstance] = useCollaborationMode('krpanoRef');
    const [tourEventStats, setTourEventState] = useTourEventState('state');

    const {
      dollhouseXml,
      //
      onClickHotspot,
      onStopHotspotSound,
      onStartHotspotSound,
      onChangeAutoChangeScene,
      onRemoveAllChevronHotspot,
      onToggleModeMultipleStaging,
      // onGenerateXMLMenuThumbnailOnVR,
    } = useKrpano({ krpanoRef: krpanoRefState });

    const urlTour = useMemo(
      () =>
        sceneSelected?.media?.src_preview ||
        sceneSelected?.media?.src_thumb ||
        sceneSelected?.media?.src,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [sceneSelected?.id],
    );

    const isShowMultipleStagingThumb = useMemo(
      () =>
        sceneSelected?.mode === 'staging' &&
        sceneSelected?.staging?.length &&
        sceneSelected?.staging.filter(item => item.media).length &&
        !sceneSelected?.configStaging?.modeDisplay,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [sceneSelected?.id],
    );

    const sceneChildsMultipleStaging = useMemo(
      () =>
        (
          (sceneSelected?.mode === 'staging' &&
          !!sceneSelected?.configStaging?.modeDisplay
            ? sceneSelected?.staging
            : []) || []
        )
          .filter(item => item.media)
          .map(item => ({
            ...item,
            urlTour:
              item?.media?.src_preview ||
              item?.media?.src_thumb ||
              item?.media?.src,
            config: sceneSelected?.config,
            configPlugin: sceneSelected?.configPlugin,
          })) as (I_Scene & { urlTour: string })[],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [sceneSelected?.id],
    );

    const onChangeSceneById = useCallback(
      id => {
        const nextscene = listScene.find(scene => scene.id === id);
        if (nextscene) onChangeScene(nextscene);
      },
      [listScene, onChangeScene],
    );

    /**
     * NOTE: destroy sound prevScene when change scene
     */
    useEffect(() => {
      if (prevSceneSelected) {
        onStopHotspotSound();
        onRemoveAllChevronHotspot(prevSceneSelected.hotspots);
        onToggleModeMultipleStaging(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prevSceneSelected]);

    /**
     * NOTE: auto play sound when change scene (only when allow sound autoplay)
     */
    useEffect(() => {
      if (configSound.on && sceneSelected) onStartHotspotSound();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sceneSelected?._id, configSound]);

    // useEffect(() => {
    //   if (
    //     !isLoadedVRMenuRef.current &&
    //     krpanoRefState &&
    //     tourEventStats?.isLoaded
    //   ) {
    //     const xml = onGenerateXMLMenuThumbnailOnVR();
    //     krpanoRefState.loadXml(xml);
    //     krpanoRefState.call(
    //       `hide_selected_scene_from_menu_vr(${sceneSelected?.id})`,
    //     );
    //     isLoadedVRMenuRef.current = true;
    //   }
    //   // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [tourEventStats?.isLoaded, krpanoRefState]);

    // useEffect(() => {
    //   if (sceneSelected && isLoadedVRMenuRef.current)
    //     krpanoRefState?.call(
    //       `hide_selected_scene_from_menu_vr(${sceneSelected.id})`,
    //     );
    //   // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [sceneSelected?._id]);

    return (
      <>
        <BeforeAfterSlider
          mode={sceneSelected?.configStaging?.modeDisplay ?? ''}
          scenes={
            tourEventStats?.isActiveModeMultipleStaging
              ? sceneChildsMultipleStaging
              : []
          }
          nadirConfig={nadirConfig || undefined}
          firstPane={
            <KrpanoViewer
              ref={node => {
                if (!krpanoRef.current) {
                  krpanoRef.current = node;
                  setKrpanoRefState(krpanoRef.current);
                  setIsTourRendered(true);
                }
              }}
              nadirConfig={nadirConfig || undefined}
              isLoaded={tourEventStats?.isLoaded}
              listScene={listScene}
              urlTour={urlTour || ''}
              dollhouseXml={dollhouseXml}
              setIsLoaded={isLoaded => {
                setTourEventState(prev => ({ ...prev, isLoaded }));
                if (isLoaded) setKrpanoInstance(krpanoRefState);
                else if (!isLoaded) setKrpanoInstance(null);
              }}
              krpanoId={ID_KRPANO_CONTAINER}
              onClickHotspot={onClickHotspot}
              divId="krpanoContainerSnapshot"
              scene={sceneSelected || undefined}
              styleContainer={{
                height: '100%',
                width:
                  sceneSelected?.configStaging?.modeDisplay === 'split'
                    ? '100%'
                    : '100vw',
              }}
              onChangeAutoChangeScene={onChangeAutoChangeScene}
              isTurnOffFollowMode={
                !!sceneChildsMultipleStaging.length || !configFollowMode.enable
              }
              onChangeScene={onChangeSceneById}
            />
          }
        />
        {isShowMultipleStagingThumb && (
          <MultipleStagingThumb
            scenes={
              sceneSelected?.staging?.map(item => ({
                ...item,
                config: sceneSelected?.config,
              })) || []
            }
            callKrpano={krpanoRef.current?.call}
          />
        )}
      </>
    );
  },
);
